import React, {useState} from 'react';
import AppButton from '@common/components/app-button';
import {ModalIds} from '@common/models/enums/modal-ids.enum';
import useModalWithData from '@common/hooks/use-modal-with-data';
import {classNames} from '@utils/class-names';
import {ReactComponent as DeleteIcon} from '@assets/icons/delete.svg';
import {useLoading} from "../../core/hooks/use-loading.hook";
import AppModal from "../../core/components/app-modal";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {stripHtmlToText} from "../helpers/strip-html-to-text";
import {useArticleForm} from "../hooks/use-article-form.hook";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";

export interface DeleteArticleModalData {
    articleId?: string;
    articleTitle?: string;
    onSuccess?: () => void;
}

const DeleteArticleModal: React.FC = () => {
    const {hideModal, data, isOpenModal} = useModalWithData<DeleteArticleModalData>(ModalIds.ARTICLE_DELETE_CONFIRM);
    const {deleteDraftArticle, deleteArticle} = useArticleForm();
    const {isLoading} = useLoading();

    const [confirmationStep, setConfirmationStep] = useState<'first' | 'second'>('first');
    const [confirmationText, setConfirmationText] = useState('');

    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');

    const modalData = data as DeleteArticleModalData | null;

    if (!modalData) return null;

    const {articleId, articleTitle, onSuccess} = modalData;

    const isFirstStep = confirmationStep === 'first';
    const isDeleting = isLoading('USE_ARTICLE_API');
    const isDraftArticle = !articleId;

    const displayTitle = articleTitle ? stripHtmlToText(convertLexicalJsonToHtml(articleTitle)) : undefined;
    const articleType = isDraftArticle ? 'szkic artykułu' : 'artykuł';

    const expectedConfirmationText = 'USUŃ';
    const isConfirmationTextValid = confirmationText === expectedConfirmationText;

    const handleFirstConfirm = () => {
        setConfirmationStep('second');
    };

    const handleSecondConfirm = async () => {
        if (!isConfirmationTextValid) return;
        try {
            if (articleId) {
                await deleteArticle(articleId);
            } else {
                await deleteDraftArticle();
            }
            hideModal();
            handleBackToFirstStep();
            onSuccess?.();
        } catch (error) {
            console.error('Błąd podczas usuwania artykułu:', error);
        }
    };

    const handleCancel = () => {
        closeModal(() => {
            setConfirmationStep('first');
            setConfirmationText('');
            hideModal();
        });
    };

    const handleBackToFirstStep = () => {
        setConfirmationStep('first');
        setConfirmationText('');
    };

    return (
        <AppModal
            id={ModalIds.ARTICLE_DELETE_CONFIRM}
            key={ModalIds.ARTICLE_DELETE_CONFIRM}
            zIndex={50}
            forceFixed={true}>
            <animated.div
                style={springProps}
                className="h-full w-full flex flex-col bg-surface-level-1 rounded-2xl border border-outline-mild shadow-lvl-2 py-4 px-6 md:max-w-md md:min-w-[400px]"
            >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                        <DeleteIcon className="w-5 h-5 text-error"/>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-on-surface">
                            {isFirstStep ? `Usuń ${articleType}` : 'Potwierdź usunięcie'}
                        </h3>
                        <p className="text-sm text-on-surface/70">
                            {isFirstStep ? 'Ta akcja jest nieodwracalna' : 'Wpisz tekst aby potwierdzić'}
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="mb-6">
                    {isFirstStep ? (
                        <div className="space-y-3">
                            <p className="text-on-surface/80 text-sm">
                                {isDraftArticle
                                    ? 'Czy na pewno chcesz usunąć szkic artykułu?'
                                    : 'Czy na pewno chcesz usunąć artykuł:'
                                }
                            </p>
                            {displayTitle && (
                                <div className="bg-surface-level-2 p-2 rounded-lg border border-outline-mild">
                                    <p className="font-semibold text-sm text-on-surface truncate">
                                        {displayTitle}
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-on-surface/80 text-sm">
                                Aby potwierdzić usunięcie, wpisz <span className="font-bold text-error">USUŃ</span> w
                                polu poniżej:
                            </p>
                            <div>
                                <input
                                    type="text"
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                                    placeholder="Wpisz: USUŃ"
                                    className={classNames(
                                        "w-full p-2 border text-sm font-semibold rounded-lg transition-colors",
                                        "focus:outline-none focus:ring-2 focus:ring-primary/20",
                                        isConfirmationTextValid
                                            ? "border-success bg-success/5"
                                            : "border-outline-mild bg-surface-level-2"
                                    )}
                                    autoFocus
                                />
                                {confirmationText && !isConfirmationTextValid && (
                                    <p className="text-sm text-error mt-1">
                                        Wpisz dokładnie: USUŃ
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 flex-wrap md:flex-nowrap">
                    {isFirstStep ? (
                        <>
                            <AppButton
                                onClick={handleCancel}
                                className="flex-1 font-semibold border-2 border-on-surface hover:bg-on-surface/5"
                                disabled={isDeleting}
                            >
                                Anuluj
                            </AppButton>
                            <AppButton
                                onClick={handleFirstConfirm}
                                className="flex-1 bg-error text-white font-semibold hover:bg-error/80"
                                disabled={isDeleting}
                            >
                                {isDraftArticle ? 'Usuń szkic' : 'Usuń artykuł'}
                            </AppButton>
                        </>
                    ) : (
                        <>
                            <AppButton
                                onClick={handleBackToFirstStep}
                                className="flex-1 font-semibold border-2 border-on-surface hover:bg-on-surface/5"
                                disabled={isDeleting}
                            >
                                Wróć
                            </AppButton>
                            <AppButton
                                onClick={handleSecondConfirm}
                                className="flex-1 bg-error text-white font-semibold hover:bg-error/80"
                                disabled={!isConfirmationTextValid || isDeleting}
                                loading={isDeleting}
                                loaderColor="#fff"
                            >
                                {isDeleting ? 'Usuwanie...' : `Usuń ${isDraftArticle ? 'szkic' : 'artykuł'}`}
                            </AppButton>
                        </>
                    )}
                </div>
            </animated.div>
        </AppModal>
    );
};

export default DeleteArticleModal;
